import { useState, useMemo, useContext, useEffect, useRef, useLayoutEffect } from 'react';
import { useRouter } from 'next/router';
import type { ReactElement } from 'react';
import { useTranslations } from 'next-intl';
import { GetServerSidePropsContext } from 'next';
import { Layout } from '@/components';
import CommonHead from '@/components/CommonHead';
import {
  CommonHeader,
  ThreadDetail,
  ThreadDetailAction,
  ThreadDetailActionEnum,
  ThreadUser,
  ThreadHotPost,
  ThreadComments,
  BannerAd,
  ThreadBlockItem,
  EmptyThreadTips,
  ShareModal,
  PopoverCommonModal,
  Toast,
} from '@/components';
import { IThreadDetailActionRef } from '@/components/ThreadDetailAction';
import { IUserLoginInfo, ACCOUNT_ROLE } from '@/api/user';
import { getNavsList, ITopLevelNav } from '@/api/nav';
import {
  IGetThreadDetailRes,
  getThreadDetailInfo,
  likeThread,
  unlikeThread,
  IError,
  operateThreadByAdmin,
  AdminOperationEnum,
  DisplayOrderEnum,
  ICommentListRes,
} from '@/api/detail';
import { ThreadTagType } from '@/components/ThreadTag/mIndex';
import { ContentTypeEnum, getThreadReply, ThreadReplySortEnum } from '@/api/thread';
import {
  collectThread,
  uncollectThread,
  reportContent,
  getEmojiList,
  IEmoji,
  getEnums,
  ENUM_KEYS,
} from '@/api/common';
import useLoginAuth from '@/hooks/useLoginAuth';
import { pageBaseUrl } from '@/constants/url';
import { flatEmojies } from '@/utils/thread';
import { StoreContext } from '@/context';
import { ActionTypeValues } from '@/context/actionTypes';
import namespace from '@/context/namespace';
import Styles from './index.module.scss';
import { ThreadCommentsExpose } from '@/components/ThreadComments';
import userCenter from '@/constants/userCenter';
import { createMarkup } from '@/utils/thread';
import Logger from '@/utils/logger';
import { PromiseState } from '@/constants';
import { getErrorMessage } from '@/constants/errorCodeMap';
import { IThreadDetailCtx, ThreadDetailCtx } from '@/context/threadDetail';
import CommonDialog from '@/components/CommonDialog/mIndex';
import useSls, { MetricTypes, statusTypes } from '@/hooks/useSls';
import { getResourceUsage } from '@/utils/resourceUsage';

export enum TooltipTypeEnum {
  share = 'share',
  extend = 'extend',
}

enum ServerSideErrorCodes {
  pcEmptyTid = 'pc_detail_empty_tid_error',
  pcEmptyContent = 'pc_detail_empty_content_error',
  pcGetThreadDetail = 'pc_detail_api_error',
  pcGetDeleteReason = 'pc_detail_delete_reason_api_error',
  pcGetEmoji = 'pc_detail_emoji_api_error',
  pcGetReply = 'pc_detail_reply_api_error',
};

interface IErrorInfo {
  name: ServerSideErrorCodes,
  code?: string,
  message?: string,
}

interface IDetail {
  tid: string;
  account: IUserLoginInfo;
  detailData: IGetThreadDetailRes['item'];
  replys: {
    firstTwoReplys: ICommentListRes[];
    totalReplys: number;
  };
  emojies: IEmoji[];
  errors: IErrorInfo[];
  navsList: ITopLevelNav[];
  deleteReasons: string[];
  saltkey: string;
}

const Detail = ({
  tid,
  account,
  detailData,
  replys,
  emojies,
  errors = [],
  saltkey,
  deleteReasons = [],
  navsList = [],
}: IDetail) => {
  const { state, dispatch } = useContext(StoreContext);
  const { isLogin } = useLoginAuth(account);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const threadCommentsRef = useRef<ThreadCommentsExpose>(null);
  const shareActionRef = useRef<IThreadDetailActionRef>(null)
  const moreActionRef = useRef<IThreadDetailActionRef>(null)
  const [richTextCont, setRichTextCont] = useState<any>(null);
  const router = useRouter();
  const { csr, offset, adminToken, adminPostToken } = router.query;
  const hasOffsetParam = useMemo(() => Object.prototype.hasOwnProperty.call(router.query, 'offset'), [router.query]);
  const commentOffset = useMemo(() => {
    const rawOffset = Array.isArray(offset) ? offset[0] : offset;
    const parsed = Number(rawOffset);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [offset]);
  const detailAdminToken = useMemo(() => {
    const rawToken = Array.isArray(adminToken) ? adminToken[0] : adminToken;
    return typeof rawToken === 'string' ? rawToken : '';
  }, [adminToken]);
  const commentAdminPostToken = useMemo(() => {
    const rawToken = Array.isArray(adminPostToken) ? adminPostToken[0] : adminPostToken;
    return typeof rawToken === 'string' ? rawToken : '';
  }, [adminPostToken]);

  // 添加状态来存储 CSR 加载的数据
  const [csrData, setCsrData] = useState({
    detailData,
    replys,
    emojies,
    navsList,
    deleteReasons,
  });
  // CSR 数据加载
  useEffect(() => {
    const loadCsrData = async () => {
      if (csr === 'true') {
        try {
          const [detail, deleteReason, emoji, reply, navs] = await Promise.all([
            getThreadDetailInfo(false, tid as string, {}, 'desktop', detailAdminToken || undefined),
            getEnums(false, { key: ENUM_KEYS.DELETE_THREAD_REASONS }),
            getEmojiList(false),
            getThreadReply(
              tid as string,
              0,
              10,
              false,
              ThreadReplySortEnum.likesHot,
              commentAdminPostToken ? { adminPostToken: commentAdminPostToken } : {}
            ),
            getNavsList(false),
          ]);

          setCsrData({
            detailData: detail?.item || {},
            replys: {
              firstTwoReplys: reply?.ext?.previewPost
                ? [reply.ext.previewPost, ...(reply?.items || [])]
                : (reply?.items || []),
              totalReplys: reply?.total_items || 0,
            },
            emojies: flatEmojies(emoji),
            navsList: navs,
            deleteReasons: deleteReason,
          });
        } catch (error) {
          console.error('Failed to load CSR data:', error);
        }
      }
    };

    loadCsrData();
  }, [csr, tid, detailAdminToken, commentAdminPostToken]);

  // 使用 CSR 数据或者 props 数据
  const {
    detailData: currentDetailData,
    replys: currentReplys,
    emojies: currentEmojies,
    navsList: currentNavsList,
    deleteReasons: currentDeleteReasons,
  } = csr === 'true' ? csrData : {
    detailData,
    replys,
    emojies,
    navsList,
    deleteReasons,
  };

  const t = useTranslations();

  const { reportSlsData } = useSls();

  const { track, TRACK_EVENTS, extractThreadTags } = state[namespace.app].sensors || {};

  useEffect(() => {
    if (!router.isReady) return;
    const root = document.querySelector('#__next') as HTMLElement | null;
    if (!hasOffsetParam) {
      root?.scrollTo(0, 0);
      return;
    }

    const timer = window.setTimeout(() => {
      const replyAnchor = document.getElementById('reply-list');
      if (replyAnchor) {
        replyAnchor.scrollIntoView({ block: 'start' });
      }
    }, 0);

    return () => window.clearTimeout(timer);
  }, [router.isReady, router.asPath, hasOffsetParam]);

  const threadOpenTrack = () => {
    track?.(TRACK_EVENTS?.[namespace.detail].threadOpen.name, {
      thread_name: currentDetailData.subject,
      thread_tid: currentDetailData.tid,
      thread_tag_name: extractThreadTags?.(currentDetailData.forum_types),
      account_slug: currentDetailData.authorid || currentDetailData?.author?.id,
      is_featured: !!currentDetailData.digest,
      list_name: (currentDetailData.forum_types || [])[0]?.name || t('btn_home'),
    }, namespace.detail);
  }

  useEffect(() => {
    if (track) {
      threadOpenTrack();
    }
  }, [track])

  useEffect(() => {
    if (state.detail.ext?.tips?.message) {
      Toast.show({
        rootClassName: Styles.toastRootCls,
        type: 'light',
        className: "publishSuccessTipsWrapper",
        content: t("tips_post_success"),
        renderIcon: <i className={Styles.publishSuccessIcon} />
      });
      setTimeout(() => {
        Toast.clear();
        Toast.show({
          content: state.detail.ext?.tips?.message,
        });
        dispatch({
          type: ActionTypeValues.PUBLISH_SUCCESS,
          namespace: namespace.detail,
          payload: {
            ext: null
          },
        });
      }, 2000)
    }

    if (errors.length > 0) {
      errors.forEach(error => {
        const errorObj: any = new Error(error.message);
        errorObj.name = error.name;
        window.Sentry?.captureException(errorObj, {
          tags: {
            type: 'BlankScreen',
            code: error.code,
          }
        });
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (currentDetailData.special != 6) {
      setRichTextCont(createMarkup(currentDetailData.message as any, false));
    }
  }, [currentDetailData.special]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch({
      namespace: namespace.app,
      type: ActionTypeValues.UPDATE_ACCOUNT,
      payload: account
    })
    dispatch({
      namespace: namespace.detail,
      type: ActionTypeValues.UPDATE_EMOJIES,
      payload: currentEmojies
    })
    dispatch({
      namespace: namespace.detail,
      type: ActionTypeValues.UPDATE_DELETEREASONS,
      payload: currentDeleteReasons
    })
    dispatch({
      namespace: namespace.detail,
      type: ActionTypeValues.UPDATE_TOTALREPLYS,
      payload: currentReplys.totalReplys
    })
  }, [account, currentEmojies, currentDeleteReasons, currentReplys]) // eslint-disable-line react-hooks/exhaustive-deps

  const threadTags = useMemo(() => {
    const { displayorder, digest } = currentDetailData;
    return [
      ...(displayorder > 0 ? [ThreadTagType.top] : []),
      ...(!!digest ? [ThreadTagType.selected] : []),
    ];
  }, [currentDetailData.displayorder, currentDetailData.digest]); // eslint-disable-line react-hooks/exhaustive-deps

  const threadContents = useMemo(() => {
    return currentDetailData.special != 6
      ? []
      : ((currentDetailData.message as any) || [])
        .filter(({ type, desc }: { type: ContentTypeEnum, desc: string }) => type !== ContentTypeEnum.text || (type === ContentTypeEnum.text && !!desc));
  }, [currentDetailData.message]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUserActionCb = (isActive: boolean, type: ThreadDetailActionEnum, visible = false) => {
    if (type === ThreadDetailActionEnum.like) {
      if (!isLogin) {
        track?.(TRACK_EVENTS?.[namespace.detail].threadLikeResult.name, {
          thread_name: currentDetailData.subject,
          is_success: false,
          fail_reason: '未登录',
          thread_tid: currentDetailData.tid,
          thread_tag_name: extractThreadTags?.(currentDetailData.forum_types),
          account_slug: currentDetailData.authorid || currentDetailData?.author?.id,
          button_name: isActive ? 'Like' : 'Unlike',
        }, namespace.detail);
        return;
      }
      reportSlsData([
        ['reportMetric', MetricTypes.likeStatus, statusTypes.click],
      ])
      const func = isActive ? likeThread : unlikeThread;
      func({ tid: currentDetailData.tid }).then(() => {
        track?.(TRACK_EVENTS?.[namespace.detail].threadLikeResult.name, {
          thread_name: currentDetailData.subject,
          is_success: true,
          thread_tid: currentDetailData.tid,
          thread_tag_name: extractThreadTags?.(currentDetailData.forum_types),
          account_slug: currentDetailData.authorid || currentDetailData?.author?.id,
          button_name: isActive ? 'Like' : 'Unlike',
        }, namespace.detail);
        reportSlsData([
          ['reportMetric', MetricTypes.likeStatus, statusTypes.success],
        ])
      })
      .catch((e) => {
        track?.(TRACK_EVENTS?.[namespace.detail].threadLikeResult.name, {
          thread_name: currentDetailData.subject,
          is_success: false,
          fail_reason: getErrorMessage(e?.code) || e?.message,
          thread_tid : currentDetailData.tid,
          thread_tag_name: extractThreadTags?.(currentDetailData.forum_types),
          account_slug: currentDetailData.authorid || currentDetailData?.author?.id,
          button_name: isActive ? 'Like' : 'Unlike',
        }, namespace.detail);
        reportSlsData([
          ['reportMetric', MetricTypes.likeStatus, statusTypes.fail],
          ['setEnvironment',{
            [MetricTypes.failReason]: e?.message || e?.code || '未知原因',
          }],
        ])
      });
    } else if (type === ThreadDetailActionEnum.collect) {
      if (!isLogin) {
        track?.(TRACK_EVENTS?.[namespace.detail].threadCollectResult.name, {
          thread_name: currentDetailData.subject,
          is_success: false,
          fail_reason: '未登录',
          thread_tid: currentDetailData.tid,
          thread_tag_name: extractThreadTags?.(currentDetailData.forum_types),
          account_slug: currentDetailData.authorid || currentDetailData?.author?.id,
          button_name: isActive ? 'Collected' : 'Uncollected',
        }, namespace.detail);
        return;
      }
      reportSlsData([
        ['reportMetric', MetricTypes.collectStatus, statusTypes.click],
      ])
      const func = isActive ? collectThread : uncollectThread;
      func({ id: currentDetailData.tid, idtype: 'tid', title: currentDetailData.subject }).then(() => {
        track?.(TRACK_EVENTS?.[namespace.detail].threadCollectResult.name, {
          thread_name: currentDetailData.subject,
          is_success: true,
          thread_tid: currentDetailData.tid,
          thread_tag_name: extractThreadTags?.(currentDetailData.forum_types),
          account_slug: currentDetailData.authorid || currentDetailData?.author?.id,
          button_name: isActive ? 'Collected' : 'Uncollected',
        }, namespace.detail);
        reportSlsData([
          ['reportMetric', MetricTypes.collectStatus, statusTypes.success],
        ])
      })
      .catch((e) => {
        track?.(TRACK_EVENTS?.[namespace.detail].threadCollectResult.name, {
          thread_name: currentDetailData.subject,
          is_success: false,
          fail_reason: getErrorMessage(e?.code) || e?.message,
          thread_tid : currentDetailData.tid,
          thread_tag_name: extractThreadTags?.(currentDetailData.forum_types),
          account_slug: currentDetailData.authorid || currentDetailData?.author?.id,
          button_name: isActive ? 'Collected' : 'Uncollected',
        }, namespace.detail);
        reportSlsData([
          ['reportMetric', MetricTypes.collectStatus, statusTypes.fail],
          ['setEnvironment',{
            [MetricTypes.failReason]: e?.message || e?.code || '未知原因',
          }],
        ])
      });
    } else if (type === ThreadDetailActionEnum.share) {
      track?.(TRACK_EVENTS?.[namespace.detail].userShareEntryButtonsClick.name, {
        thread_name: currentDetailData.subject,
        thread_tid: currentDetailData.tid,
        thread_tag_name: extractThreadTags?.(currentDetailData.forum_types),
        account_slug: currentDetailData.authorid || currentDetailData?.author?.id,
      }, namespace.detail);
    }
  }

  const handleReport = async (reason: string, desc?: string) => {
    reportContent({
      fid: currentDetailData.fid,
      message: desc ? `${reason}-${desc}` : reason,
      url: window?.location?.href,
    }).then(() => {
      setReportModalVisible(false);
      Toast.show({
        content: t('tips_report_success'),
      });
    }).catch((e) => {
      Toast.show({
        content: e?.message || t('error_common_server_error'),
      });
      setReportModalVisible(false);
    });
  };

  // 管理员：对任何页面，可编辑/可删除/可回复；
  // 作者本人：对自己发布帖子可编辑/可回复；
  // 非作者普通用户对其他用户发布的帖子：可举报/可回复。

  const moreActions = useMemo(() => {
    const isSelf = account.id === currentDetailData.authorid;

    if (account.role === ACCOUNT_ROLE.ADMIN) {
      return [{
        key: 'delete',
        label: t('btn_delete'),
        callback: () => {
          setDeleteModalVisible(true);
        },
      }, {
        key: 'edit',
        label: t('btn_edit'),
        callback: () => {
          // 编辑按钮点击上报
          track?.(TRACK_EVENTS?.[namespace.detail].uploadButtonsClick.name, {
            position: 'Edit',
            is_success: true,
          }, namespace.detail)
          router.push(`${pageBaseUrl}/edit?source=thread&tid=${tid}`);
        },
      }];
    } else if (isSelf) {
      return [{
        key: 'default',
        label: t('btn_edit'),
        callback: () => {
          // 编辑按钮点击上报
          track?.(TRACK_EVENTS?.[namespace.detail].uploadButtonsClick.name, {
            position: 'Edit',
            is_success: true,
          }, namespace.detail)
          router.push(`${pageBaseUrl}/edit?source=thread&tid=${tid}`);
        },
      }];
    } else {
      return [{
        key: 'report',
        label: t('btn_report'),
        callback: () => {
          setReportModalVisible(true);
        },
      }];
    }
  }, [account.id, account.role, currentDetailData.authorid]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderMoreActionModal = () => {
    return (
      <PopoverCommonModal className={Styles.modalRoot}>
        <ul className={Styles.list}>
          {
            moreActions.map(({ key, label, callback }) => <li onClick={() => {
              moreActionRef.current?.hidePopover(false);
              callback();
            }} key={key}>{label}</li>)
          }
        </ul>
      </PopoverCommonModal>
    )
  }

  const handleDeleteThread = (reason: string, desc?: string) => {
    const message = desc ? `${reason}-${desc}` : reason;
    operateThreadByAdmin({
      id: tid,
      message: message,
      operate: AdminOperationEnum.DELETE,
    }).then((res) => {
      if (res) {
        setDeleteModalVisible(false);
        Toast.show({
          content: res,
        });
      } else {
        setDeleteModalVisible(false);
        router.push('/');
      }
    });
  };

  const handleCommentClickCb = () => {
    if (account.status !== PromiseState.PENDING) {
      if (isLogin) {
        document.querySelectorAll('#reply-list')?.[0]?.scrollIntoView({ behavior: 'smooth' });
        threadCommentsRef.current?.onFocus();
      } else {
        window.open(userCenter().login, '_self');
      }
    }
  }

  return (
    <ThreadDetailCtx.Provider value={currentDetailData as unknown as IThreadDetailCtx}>
      <div className={Styles.root}>
        <CommonHead />
        <CommonHeader
          account={account}
          fid={currentDetailData.fid}
          navsList={currentNavsList}
          hasNav={true}
          saltkey={saltkey}
        />
        <section className={Styles.main}>
          <section className={Styles.left}>
            {
              !!errors.length ? <EmptyThreadTips /> :
                <>
                  <section className={Styles.detail}>
                    <ThreadDetail
                      author={currentDetailData.author}
                      subject={currentDetailData.subject}
                      tags={threadTags}
                      special={currentDetailData.special}
                      contents={threadContents}
                      richTextCont={richTextCont}
                      isPrivate={currentDetailData?.displayorder == DisplayOrderEnum.PRIVATE}
                      relatedInfo={{
                        views: currentDetailData.views,
                        comments: currentDetailData.comments,
                        dateline: currentDetailData.dateline,
                        devices: currentDetailData.devices,
                        subjects: currentDetailData.forum_types,
                        channel_tags: currentDetailData.forum_channel_tags,
                      }}
                    />
                    {/* 点赞收藏 */}
                    <div className={Styles.actionContainer}>
                      <div className={Styles.actionWrapper}>
                        <ThreadDetailAction
                          tooltipText={t("tips_thread_like")}
                          className={Styles.actionItem}
                          type={ThreadDetailActionEnum.like}
                          defaultCount={currentDetailData.praise}
                          isActiveDefault={currentDetailData.praised}
                          account={account}
                          isLogin={isLogin}
                          handleUserActionCb={(isActive) => handleUserActionCb(isActive, ThreadDetailActionEnum.like)}
                        />
                      </div>
                      <div className={Styles.actionWrapper}>
                        <ThreadDetailAction
                          tooltipText={t("tips_thread_collect")}
                          className={Styles.actionItem}
                          type={ThreadDetailActionEnum.collect}
                          defaultCount={currentDetailData.favtimes}
                          isActiveDefault={currentDetailData.favorite}
                          account={account}
                          isLogin={isLogin}
                          handleUserActionCb={(isActive) => handleUserActionCb(isActive, ThreadDetailActionEnum.collect)}
                        />
                      </div>
                      {/* 回复数量 */}
                      <div className={Styles.actionWrapper}>
                        <ThreadDetailAction
                          tooltipText={t("tips_thread_comment")}
                          className={Styles.actionItem}
                          type={ThreadDetailActionEnum.comment}
                          defaultCount={currentDetailData.replies}
                          value={state.detail.totalReplys}
                          account={account}
                          handleUserActionCb={handleCommentClickCb}
                        />
                      </div>
                      <div className={Styles.actionWrapper}>
                        <ThreadDetailAction
                          popoverNode={
                            <ShareModal
                              title={currentDetailData.subject}
                              forumTypes={currentDetailData.forum_types}
                              accountSlug={currentDetailData.authorid || currentDetailData?.author?.id}
                              hidePopover={(vis) => shareActionRef.current?.hidePopover(vis)}
                              postTid={currentDetailData.tid}
                              onShareHandler={() => shareActionRef.current?.onShareCountAdd()}
                            />
                          }
                          tooltipText={t("tips_thread_share")}
                          className={Styles.actionItem}
                          type={ThreadDetailActionEnum.share}
                          defaultCount={currentDetailData.sharetimes}
                          title={currentDetailData.subject}
                          account={account}
                          handleUserActionCb={(isActive, visible) => handleUserActionCb(isActive, ThreadDetailActionEnum.share, visible)}
                          ref={shareActionRef}
                        />
                      </div>
                      {
                        isLogin &&
                        <div className={Styles.actionWrapper}>
                          <ThreadDetailAction
                            popoverNode={renderMoreActionModal()}
                            tooltipText={t("tips_thread_extend_more")}
                            className={Styles.actionItem}
                            type={ThreadDetailActionEnum.extend}
                            ref={moreActionRef}
                            account={account}
                          />
                        </div>
                      }
                    </div>
                  </section>

                  {/**  评论 */
                    <ThreadComments
                      replys={currentReplys}
                      tid={tid}
                      initialOffset={commentOffset}
                      adminPostToken={commentAdminPostToken}
                      title={currentDetailData.subject}
                      forumTypes={currentDetailData.forum_types}
                      authorid={currentDetailData.authorid || currentDetailData?.author?.id}
                      account={account}
                      ref={threadCommentsRef}
                    />
                  }
                  {/* 相关推荐 */}
                  <ThreadBlockItem tid={tid} subject={currentDetailData.subject} />
                </>
            }
          </section>
          <section className={Styles.right}>
            {/* 关于作者 */}
            {(!errors.length && currentDetailData.author) &&
              <ThreadUser
                author={currentDetailData.author}
              />
            }
            {/* 热帖 */}
            <ThreadHotPost />
            {/* 广告位 */}
            <BannerAd />
          </section>
        </section>
        {
          reportModalVisible && (
            <CommonDialog
              title={t('modal_title_report_reasons')}
              isReport
              confirmText={t('btn_report')}
              onConfirm={handleReport}
              onCancel={() => setReportModalVisible(false)}
            />
          )
        }
        {
          deleteModalVisible && (
            <CommonDialog
              title={t('modal_title_delete_reasons')}
              reasonsList={currentDeleteReasons}
              confirmText={t('btn_delete')}
              onConfirm={handleDeleteThread}
              onCancel={() => setDeleteModalVisible(false)}
            />
          )
        }
      </div>
    </ThreadDetailCtx.Provider>
  );
};
Detail.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  // 检查是否需要 CSR 渲染
  const { csr } = ctx.query;
  if (csr === 'true') {
    return {
      props: {
        tid: ctx.query.tid || null,
        detailData: {},
        replys: {
          firstTwoReplys: [],
          totalReplys: 0
        },
        emojies: [],
        errors: [],
        navsList: [],
        deleteReasons: [],
        saltkey: '',
        account: {},
      },
    };
  }

  const startTime = Date.now();
  try {
    const { tid } = ctx.query;
    const adminToken = typeof ctx.query.adminToken === 'string' ? ctx.query.adminToken : '';
    const adminPostToken = typeof ctx.query.adminPostToken === 'string' ? ctx.query.adminPostToken : '';

    // 记录 500 错误 并重定向到 CSR
    if (ctx.res?.statusCode === 500) {
      Logger.error({
        type: 'SSR_PC_DETAIL_PAGE_500_ERROR',
        path: ctx.resolvedUrl,
        message: JSON.stringify({
          ...getResourceUsage(),
          headers: ctx.req?.headers,
          statusCode: 500,
        }),
        user_agent: ctx.req?.headers?.['user-agent'],
        referer: ctx.req?.headers?.['referer'],
      });
      // 重定向到 CSR
      const destination = `pro/${ctx.resolvedUrl}${ctx.resolvedUrl.includes('?') ? '&' : '?'}csr=true`
      return {
        redirect: {
          destination,
          permanent: false,
        },
      };
    }

    /** 正文内容 */
    let threadDetailData = {}, deleteReasons: string[] = [];
    /** 前两条回复 */
    let replys: {
      firstTwoReplys: ICommentListRes[];
      totalReplys: number;
    } = {
      firstTwoReplys: [],
      totalReplys: 0
    }
    /** 表情包 */
    let emojies: IEmoji[] = [];
    let navsList: ITopLevelNav[] = [];
    let errors: IErrorInfo[] = [];

    try {
      navsList = await getNavsList(true);
    } catch (e) {
      navsList = [];
    }

    const replyPromise = getThreadReply(
      tid as string,
      0,
      10,
      true,
      ThreadReplySortEnum.likesHot,
      {
        ...(ctx.req ? ctx.req.headers : {}),
        ...(adminPostToken ? { adminPostToken } : {}),
      },
    );
    const { detail, deleteReason, emoji, reply } = await Promise.allSettled([
      getThreadDetailInfo(true, tid as string, ctx.req ? ctx.req.headers : {}, 'desktop', adminToken),
      getEnums(true, { key: ENUM_KEYS.DELETE_THREAD_REASONS }, ctx.req ? ctx.req.headers : {}),
      getEmojiList(true, ctx.req ? ctx.req.headers : {}),
      replyPromise,
    ]).then(([detailRes, deleteReasonRes, emojiRes, replyRes]) => {
      let detailVal, deleteReasonVal, emojiVal, replyVal;

      if (detailRes?.status === 'fulfilled') {
        detailVal = detailRes?.value || [];
      } else {
        errors.push({
          name: ServerSideErrorCodes.pcGetThreadDetail,
          code: detailRes?.reason?.code || '',
          message: detailRes?.reason?.message || '',
        })
      }
      if (deleteReasonRes?.status === 'fulfilled') {
        deleteReasonVal = deleteReasonRes?.value || [];
      } else {
        errors.push({
          name: ServerSideErrorCodes.pcGetDeleteReason,
          code: deleteReasonRes?.reason?.code || '',
          message: deleteReasonRes?.reason?.message || '',
        })
      }
      if (emojiRes?.status === 'fulfilled') {
        emojiVal = flatEmojies(emojiRes?.value) || [];
      } else {
        errors.push({
          name: ServerSideErrorCodes.pcGetEmoji,
          code: emojiRes?.reason?.code || '',
          message: emojiRes?.reason?.message || '',
        })
      }
      if (replyRes?.status === 'fulfilled') {
        const previewPost = replyRes?.value?.ext?.previewPost;
        const replyItems = replyRes?.value?.items || [];
        replyVal = {
          firstTwoReplys: previewPost ? [previewPost, ...replyItems] : replyItems,
          totalReplys: replyRes?.value.total_items || 0
        };
      } else {
        errors.push({
          name: ServerSideErrorCodes.pcGetReply,
          code: replyRes?.reason?.code || '',
          message: replyRes?.reason?.message || '',
        })
      }
      return {
        detail: detailVal || [],
        deleteReason: deleteReasonVal || [],
        emoji: emojiVal || [],
        reply: replyVal || replys,
      };
    });
    threadDetailData = detail;
    deleteReasons = deleteReason;
    emojies = emoji;
    replys = reply;

    if (!tid) {
      errors.push({
        name: ServerSideErrorCodes.pcEmptyTid,
        message: 'BlankScreen',
      })
    } else if (tid && !Object.keys((threadDetailData as IGetThreadDetailRes)?.item || {}).length) {
      errors.push({
        name: ServerSideErrorCodes.pcEmptyContent,
        message: 'BlankScreen',
      })
    }

    const matchCookies = ctx.req.headers.cookie?.match(/H5VF_2132_saltkey=(\w+)/);

    if (errors.length > 0) {
      Logger.error({
        type: 'SSR_PC_DETAIL_PAGE_ERROR',
        path: ctx.resolvedUrl,
        user_agent: ctx.req.headers['user-agent'],
        referer: ctx.req.headers['referer'],
        message: JSON.stringify({
          ...getResourceUsage(),
          headers: ctx.req?.headers,
          errorCtx: errors
        }),
      });
    } else {
      Logger.info({
        type: 'SSR_PC_DETAIL_PAGE',
        path: ctx.resolvedUrl,
        message: JSON.stringify({
          renderTime: Date.now() - startTime,
        }),
      });
    }

    return {
      props: {
        tid: tid || null,
        detailData: (threadDetailData as IGetThreadDetailRes)?.item || {},
        replys,
        emojies,
        errors,
        navsList,
        deleteReasons,
        saltkey: matchCookies && matchCookies.length >= 2 ? matchCookies[1] : '',
        account: {},
      },
    };
  } catch (error) {
    // 捕获错误
    Logger.error({
      type: 'SSR_PC_DETAIL_PAGE_FATAL_ERROR',
      path: ctx.resolvedUrl,
      message: JSON.stringify({
        ...getResourceUsage(),
        headers: ctx.req?.headers,
        errorCtx: error instanceof Error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : error,
      }),
      user_agent: ctx.req?.headers?.['user-agent'],
      referer: ctx.req?.headers?.['referer'],
    });
    // 重定向到 CSR
    const destination = `/pro${ctx.resolvedUrl}${ctx.resolvedUrl.includes('?') ? '&' : '?'}csr=true`
    return {
      redirect: {
        destination,
        permanent: false,
      },
    };
  }
}

export default Detail;
